import React, { useState, useMemo } from 'react';
import { 
  Shield, Key, Plus, Sliders, Play, CheckCircle2, AlertCircle, 
  Layers, ArrowRight, UserCheck, Lock, Trash2, Edit3, Copy, X
} from 'lucide-react';
import { useSecurityStore, ABACPolicy, ABACCondition } from '../../../store/securityStore';
import { useAuthStore } from '../../../store/authStore';
import { RowActions } from '../../../components/RowActions';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

export function AuthorizationSubTab() {
  const { 
    abacPolicies, createABACPolicy, updateABACPolicy, 
    deleteABACPolicy, toggleABACPolicy, duplicateABACPolicy, 
    simulateABAC, securityAuditLogs 
  } = useSecurityStore();
  const { user } = useAuthStore();

  const isSuperAdmin = (user?.roles || []).some((r: any) => 
    typeof r === 'string' 
      ? r.toLowerCase().includes('admin') || r.toLowerCase().includes('super')
      : r.code === 'super_admin' || r.name === 'Super Admin'
  );

  const [activeSubTab, setActiveSubTab] = useState<'abac' | 'simulate' | 'audit'>('abac');

  // Policy Builder Modal
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ABACPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState({
    name: '',
    description: '',
    effect: 'DENY' as 'ALLOW' | 'DENY',
    priority: 1,
    status: 'active' as 'active' | 'inactive',
    logic: 'AND' as 'AND' | 'OR',
    conditions: [
      { field: 'resource.sensitivity', operator: 'equals', value: 'High' },
      { field: 'env.network', operator: 'not_equals', value: 'Corporate VPN' },
    ] as ABACCondition[],
  });

  // Simulator Form State
  const [simForm, setSimForm] = useState({
    userName: 'David Miller',
    department: 'Engineering',
    location: 'Remote (US)',
    clearance: 'Secret',
    resourceType: 'Financial Records',
    resourceSensitivity: 'High',
    resourceOwner: 'Finance Team',
    time: 'Off Hours',
    device: 'BYOD',
    network: 'Public Wi-Fi',
    action: 'Export',
  });
  const [simResult, setSimResult] = useState<any | null>(null);

  const handleOpenPolicyModal = (policy?: ABACPolicy) => {
    if (policy) {
      setEditingPolicy(policy);
      setPolicyForm({
        name: policy.name,
        description: policy.description,
        effect: policy.effect,
        priority: policy.priority,
        status: policy.status,
        logic: policy.logic,
        conditions: [...policy.conditions],
      });
    } else {
      setEditingPolicy(null);
      setPolicyForm({
        name: '',
        description: '',
        effect: 'DENY',
        priority: abacPolicies.length + 1,
        status: 'active',
        logic: 'AND',
        conditions: [{ field: 'user.department', operator: 'equals', value: 'Engineering' }],
      });
    }
    setIsPolicyModalOpen(true);
  };

  const handleAddCondition = () => {
    setPolicyForm((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'user.department', operator: 'equals', value: 'Operations' }],
    }));
  };

  const handleRemoveCondition = (index: number) => {
    setPolicyForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateCondition = (index: number, patch: Partial<ABACCondition>) => {
    setPolicyForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));
  };

  const handleSavePolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!policyForm.name.trim()) {
      toast.error('Policy name is required');
      return;
    }

    if (editingPolicy) {
      await updateABACPolicy({
        ...editingPolicy,
        name: policyForm.name.trim(),
        description: policyForm.description.trim(),
        effect: policyForm.effect,
        priority: Number(policyForm.priority),
        status: policyForm.status,
        logic: policyForm.logic,
        conditions: policyForm.conditions,
      });
    } else {
      await createABACPolicy({
        name: policyForm.name.trim(),
        description: policyForm.description.trim(),
        effect: policyForm.effect,
        priority: Number(policyForm.priority),
        status: policyForm.status,
        logic: policyForm.logic,
        conditions: policyForm.conditions,
        affectedUsersCount: Math.floor(Math.random() * 25) + 5,
      });
    }

    setIsPolicyModalOpen(false);
  };

  const handleRunSimulation = () => {
    const result = simulateABAC({
      user: {
        id: 'u-sim',
        email: 'simulated.user@teamone.com',
        department: simForm.department,
        location: simForm.location,
        clearance: simForm.clearance,
      },
      resource: {
        type: simForm.resourceType,
        sensitivity: simForm.resourceSensitivity,
        owner: simForm.resourceOwner,
      },
      env: {
        time: simForm.time,
        device: simForm.device,
        network: simForm.network,
      },
      action: simForm.action,
    });
    setSimResult(result);
  };

  const abacAuditLogs = useMemo(() => {
    return securityAuditLogs.filter((l) => l.section === 'abac');
  }, [securityAuditLogs]);

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {[
          { id: 'abac', label: `ABAC Policies (${abacPolicies.length})` },
          { id: 'simulate', label: 'Policy Evaluation Simulator' },
          { id: 'audit', label: `Authorization Audit (${abacAuditLogs.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id as any)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSubTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 1. ABAC POLICIES TABLE */}
      {activeSubTab === 'abac' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Attribute-Based Access Control (ABAC) Policy Engine
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Fine-grained contextual authorization combining user clearance, resource sensitivity, and environmental trust.
                <strong> Deny overrides allow</strong> is enforced platform-wide.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenPolicyModal()}
                className="gap-1.5 h-8.5 shrink-0"
              >
                <Plus className="size-3.5" />
                <span>Create ABAC Policy</span>
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {abacPolicies.map((pol) => (
              <div
                key={pol.id}
                className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-xs hover:border-border/80 transition-all space-y-3.5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex items-start gap-3.5">
                    <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                      pol.effect === 'DENY'
                        ? 'bg-destructive/10 text-destructive border border-destructive/20'
                        : 'bg-success/10 text-success-strong border border-success/30'
                    }`}>
                      <Lock className="size-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="size-5 rounded-md bg-muted text-muted-foreground font-mono text-3xs font-bold flex items-center justify-center" title="Priority rank">
                          #{pol.priority}
                        </span>
                        <h3 className="font-semibold text-xs sm:text-sm text-foreground">{pol.name}</h3>
                        <span
                          className={`px-2 py-0.2 rounded-full font-bold text-3xs uppercase tracking-wider ${
                            pol.effect === 'DENY'
                              ? 'bg-destructive text-destructive-foreground'
                              : 'bg-success text-success-foreground'
                          }`}
                        >
                          {pol.effect}
                        </span>
                        <span
                          className={`px-2 py-0.2 rounded-full font-semibold text-3xs uppercase tracking-wider ${
                            pol.status === 'active'
                              ? 'bg-success/10 text-success-strong border border-success/30'
                              : 'bg-muted text-muted-foreground border border-border'
                          }`}
                        >
                          {pol.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{pol.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <RowActions
                      entityName={pol.name}
                      onEdit={isSuperAdmin ? () => handleOpenPolicyModal(pol) : undefined}
                      onDuplicate={isSuperAdmin ? () => duplicateABACPolicy(pol.id) : undefined}
                      onToggleStatus={isSuperAdmin ? () => toggleABACPolicy(pol.id) : undefined}
                      isStatusActive={pol.status === 'active'}
                      onDelete={isSuperAdmin ? () => deleteABACPolicy(pol.id) : undefined}
                      onViewDetails={() => handleOpenPolicyModal(pol)}
                      readOnly={!isSuperAdmin}
                    />
                  </div>
                </div>

                {/* Conditions expression */}
                <div className="p-2.5 rounded-xl border border-border bg-muted/20 flex flex-wrap items-center gap-2 text-xs font-mono">
                  <span className="text-muted-foreground font-semibold uppercase text-3xs">Rules ({pol.logic}):</span>
                  {pol.conditions.map((c, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-card border border-border text-foreground text-3xs">
                      <strong>{c.field}</strong> {c.operator} &quot;{c.value}&quot;
                    </span>
                  ))}
                  <span className="text-muted-foreground text-3xs ml-auto">
                    Affected Users: ~{pol.affectedUsersCount} · Updated: {new Date(pol.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. POLICY EVALUATION SIMULATOR */}
      {activeSubTab === 'simulate' && (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-sm font-semibold text-foreground">
              ABAC Policy Evaluation &amp; Grant Simulator
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Simulate an access request in real-time to inspect which policy matches and verify zero-trust governance.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Simulation Inputs */}
            <div className="lg:col-span-2 p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                Simulated Request Context
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    User Department
                  </label>
                  <select
                    value={simForm.department}
                    onChange={(e) => setSimForm({ ...simForm, department: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="Engineering">Engineering</option>
                    <option value="Human Resources">Human Resources</option>
                    <option value="Finance & Accounting">Finance &amp; Accounting</option>
                    <option value="Executive">Executive</option>
                    <option value="Customer Support">Customer Support</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Resource Sensitivity
                  </label>
                  <select
                    value={simForm.resourceSensitivity}
                    onChange={(e) => setSimForm({ ...simForm, resourceSensitivity: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="High">High (Strict DLP)</option>
                    <option value="Medium">Medium (Internal)</option>
                    <option value="Low">Low (Public)</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Target Resource Type
                  </label>
                  <select
                    value={simForm.resourceType}
                    onChange={(e) => setSimForm({ ...simForm, resourceType: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="Financial Records">Financial Records</option>
                    <option value="Customer PII">Customer PII</option>
                    <option value="HR Employee Documents">HR Employee Documents</option>
                    <option value="Support Tickets">Support Tickets</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Network Context
                  </label>
                  <select
                    value={simForm.network}
                    onChange={(e) => setSimForm({ ...simForm, network: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="Corporate VPN">Corporate VPN</option>
                    <option value="Public Wi-Fi">Public Wi-Fi</option>
                    <option value="Cellular / Roaming">Cellular / Roaming</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Device Trust Tier
                  </label>
                  <select
                    value={simForm.device}
                    onChange={(e) => setSimForm({ ...simForm, device: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="Managed Laptop (MDM)">Managed Laptop (MDM)</option>
                    <option value="BYOD">BYOD / Unmanaged</option>
                  </select>
                </div>

                <div>
                  <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                    Action Attempted
                  </label>
                  <select
                    value={simForm.action}
                    onChange={(e) => setSimForm({ ...simForm, action: e.target.value })}
                    className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                  >
                    <option value="View">View Record</option>
                    <option value="Export">Mass Export</option>
                    <option value="Edit">Edit Content</option>
                    <option value="Delete">Delete Resource</option>
                  </select>
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <Button variant="primary" size="sm" onClick={handleRunSimulation} className="gap-1.5">
                  <Play className="size-3.5" />
                  <span>Evaluate Access Simulation</span>
                </Button>
              </div>
            </div>

            {/* Simulation Result Output */}
            <div className="p-5 rounded-2xl border border-border bg-card shadow-xs flex flex-col justify-between">
              <div>
                <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                  Simulation Decision Output
                </h3>

                {simResult ? (
                  <div className="py-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-base font-bold px-3 py-1 rounded-lg ${
                          simResult.decision === 'ALLOW'
                            ? 'bg-success/10 text-success-strong border border-success/30'
                            : 'bg-destructive/10 text-destructive border border-destructive/30'
                        }`}
                      >
                        {simResult.decision}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {simResult.reason}
                    </p>

                    {simResult.matchingPolicy && (
                      <div className="p-2.5 rounded-xl border border-border bg-muted/20 text-xs">
                        <span className="text-3xs uppercase font-semibold text-muted-foreground block">
                          Matched Policy
                        </span>
                        <p className="font-semibold text-foreground mt-0.5">
                          {simResult.matchingPolicy.name}
                        </p>
                        <p className="text-3xs text-muted-foreground font-mono mt-1">
                          Priority #{simResult.matchingPolicy.priority} · Logic: {simResult.matchingPolicy.logic}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-12 text-center text-xs text-muted-foreground">
                    Configure parameters on the left and click &quot;Evaluate Access Simulation&quot;.
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-border text-3xs text-muted-foreground">
                Zero-Trust Engine evaluates ABAC rules before passing request to service layer.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. AUDIT TRAIL TAB */}
      {activeSubTab === 'audit' && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
          <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
            <h3 className="font-semibold text-xs text-foreground">Authorization Policy Audit Trail</h3>
            <span className="text-xs font-semibold text-muted-foreground">{abacAuditLogs.length} events</span>
          </div>

          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {abacAuditLogs.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-xs">No authorization audit logs</div>
            ) : (
              abacAuditLogs.map((log) => (
                <div key={log.id} className="p-3.5 hover:bg-muted/10 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-foreground">{log.action}</p>
                      <p className="text-3xs text-muted-foreground mt-0.5">
                        Target: <span className="text-primary font-semibold">{log.target}</span> · Performed by {log.performedBy}
                      </p>
                    </div>
                    <span className="text-3xs text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Create / Edit Policy Drawer (Opens on Right) */}
      {isPolicyModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsPolicyModalOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 overflow-y-auto space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">
                {editingPolicy ? 'Edit ABAC Policy' : 'Create ABAC Policy'}
              </h2>
              <button
                type="button"
                onClick={() => setIsPolicyModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSavePolicy} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Policy Title</label>
                <input
                  type="text"
                  value={policyForm.name}
                  onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
                  placeholder="Deny Public Access to Production Financials"
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Description / Business Rationale</label>
                <textarea
                  value={policyForm.description}
                  onChange={(e) => setPolicyForm({ ...policyForm, description: e.target.value })}
                  rows={2}
                  className="w-full p-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary resize-none"
                  placeholder="Explain why this contextual rule is enforced..."
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Decision Effect</label>
                  <select
                    value={policyForm.effect}
                    onChange={(e) => setPolicyForm({ ...policyForm, effect: e.target.value as any })}
                    className="w-full h-8.5 px-2 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer font-bold"
                  >
                    <option value="DENY">DENY (Overrides Allow)</option>
                    <option value="ALLOW">ALLOW</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Priority Rank</label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={policyForm.priority}
                    onChange={(e) => setPolicyForm({ ...policyForm, priority: Number(e.target.value) })}
                    className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground block mb-1">Logic Gate</label>
                  <select
                    value={policyForm.logic}
                    onChange={(e) => setPolicyForm({ ...policyForm, logic: e.target.value as any })}
                    className="w-full h-8.5 px-2 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary cursor-pointer"
                  >
                    <option value="AND">AND (All match)</option>
                    <option value="OR">OR (Any match)</option>
                  </select>
                </div>
              </div>

              {/* Conditions List */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-foreground">Rule Conditions</label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddCondition} className="h-6.5 text-3xs px-2">
                    + Add Condition
                  </Button>
                </div>

                <div className="space-y-2">
                  {policyForm.conditions.map((cond, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/20">
                      <select
                        value={cond.field}
                        onChange={(e) => handleUpdateCondition(idx, { field: e.target.value })}
                        className="h-7 px-2 rounded border border-border bg-card text-3xs text-foreground outline-none cursor-pointer"
                      >
                        <option value="user.department">user.department</option>
                        <option value="user.location">user.location</option>
                        <option value="user.clearance">user.clearance</option>
                        <option value="resource.sensitivity">resource.sensitivity</option>
                        <option value="resource.type">resource.type</option>
                        <option value="resource.owner">resource.owner</option>
                        <option value="env.network">env.network</option>
                        <option value="env.device">env.device</option>
                        <option value="env.time">env.time</option>
                      </select>

                      <select
                        value={cond.operator}
                        onChange={(e) => handleUpdateCondition(idx, { operator: e.target.value as any })}
                        className="h-7 px-2 rounded border border-border bg-card text-3xs text-foreground outline-none cursor-pointer"
                      >
                        <option value="equals">equals</option>
                        <option value="not_equals">not equals</option>
                        <option value="contains">contains</option>
                        <option value="in">in list</option>
                        <option value="not_in">not in list</option>
                      </select>

                      <input
                        type="text"
                        value={cond.value}
                        onChange={(e) => handleUpdateCondition(idx, { value: e.target.value })}
                        placeholder="Expected value"
                        className="h-7 flex-1 px-2 rounded border border-border bg-card text-3xs text-foreground outline-none"
                      />

                      {policyForm.conditions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveCondition(idx)}
                          className="size-7 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center cursor-pointer"
                        >
                          <X className="size-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border">
                <Button variant="outline" size="sm" type="button" onClick={() => setIsPolicyModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  {editingPolicy ? 'Update Policy' : 'Save Policy'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
